import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../services/firebase';
import { infoPageStyles as styles } from './sharedStyles';
import { COLORS } from '../../constants';

const ReportIssue = () => {
  const [formData, setFormData] = useState({ category: '', description: '', urgency: 'medium' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Check if user is logged in
      if (!auth.currentUser) {
        setError('You must be logged in to submit a report.');
        setLoading(false);
        return;
      }

      // Call Firebase Function to send report directly
      const submitIssueReport = httpsCallable(functions, 'submitIssueReport');
      const result = await submitIssueReport({
        category: formData.category,
        description: formData.description,
        urgency: formData.urgency,
      });
      
      if (result.data.success) {
        setSubmitted(true);
        setFormData({ category: '', description: '', urgency: 'medium' });
        
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit report. Please try again or email us directly at redbirdmarket.isu@gmail.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.pageContainer}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Report an Issue</h1>
        <p style={styles.headerSubtitle}>Help us maintain a safe community</p>
      </div>

      <div style={styles.contentContainer}>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>What Can You Report?</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>Suspicious or fraudulent listings</li>
            <li style={styles.listItem}>Inappropriate user behavior</li>
            <li style={styles.listItem}>Prohibited items</li>
            <li style={styles.listItem}>Technical problems</li>
            <li style={styles.listItem}>Safety concerns</li>
            <li style={styles.listItem}>Terms of Service violations</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Submit a Report</h2>
          
          {submitted && (
            <div style={{...styles.highlight, background: '#4CAF5015', borderColor: '#4CAF50'}}>
              <p style={{...styles.text, color: '#4CAF50', margin: 0}}>
                ✓ Your report has been submitted successfully! We'll review it promptly and take appropriate action.
              </p>
            </div>
          )}
          
          {error && (
            <div style={{...styles.highlight, background: '#f4433615', borderColor: '#f44336'}}>
              <p style={{...styles.text, color: '#f44336', margin: 0}}>
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: 600, color: COLORS.primary}}>Issue Category *</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `2px solid ${COLORS.lightGray}`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <option value="">Select a category</option>
                <option value="fraud">Fraudulent Activity</option>
                <option value="inappropriate">Inappropriate Content</option>
                <option value="prohibited">Prohibited Item</option>
                <option value="harassment">Harassment</option>
                <option value="technical">Technical Issue</option>
                <option value="safety">Safety Concern</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: 600, color: COLORS.primary}}>Urgency Level *</label>
              <select
                required
                value={formData.urgency}
                onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `2px solid ${COLORS.lightGray}`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <option value="low">Low - General concern</option>
                <option value="medium">Medium - Needs attention</option>
                <option value="high">High - Urgent issue</option>
                <option value="critical">Critical - Immediate safety concern</option>
              </select>
            </div>

            <div style={{marginBottom: '24px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: 600, color: COLORS.primary}}>Description *</label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Please provide detailed information about the issue, including user IDs, listing IDs, or screenshots if applicable..."
                rows={8}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `2px solid ${COLORS.lightGray}`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  opacity: loading ? 0.6 : 1,
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                opacity: loading ? 0.6 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 6px 20px ${COLORS.primary}40`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${COLORS.primary}30`;
                }
              }}
            >
              {loading ? 'Submitting...' : 'Submit Report'}
            </button>
          </form>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Emergency Situations</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>If you are in immediate danger or witnessing a crime:</strong>
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Call 911 immediately</li>
              <li style={styles.listItem}>Contact ISU Police: (309) 438-8631</li>
              <li style={styles.listItem}>Then report the incident to us for platform action</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportIssue;
